import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { LoadingIndicator } from '../LoadingIndicator';
import { AutodeskLayerForm } from './AutodeskLayerForm';
import { useSearchSymbols } from '../../hooks/api/use-symbols.hook';
import { useSearchColorBooks } from '../../hooks/api/use-color-books.hook';
import { useTemplates } from '../../hooks/api/use-templates.hook';
import { useLineweights } from '../../hooks/api/use-lineweights.hook';
import { usePlotStyles } from '../../hooks/api/use-plot-styles.hook';
import {
  AutodeskLayerRelationship,
  AutodeskLayerApiModel,
  WithLayer,
  WithPlotStyle,
  WithLineweight,
  WithColorBook,
} from '../../../../types/models/autodesk-layer.model';
import {
  useCreateAutodeskLayer,
  useUpdateAutodeskLayer,
  useGetAutodeskLayer,
} from '../../hooks/api/use-autodesk-layers.hook';
import { getDirtyValues } from '../../utils/react-hook-form';

export const AddEditAutodeskLayer = () => {
  const navigate = useNavigate();
  const { autodeskLayerId } = useParams<{ autodeskLayerId: string }>();

  const {
    autodeskLayer,
    loading: loadingAutodeskLayer,
    error: gettingError,
  } = useGetAutodeskLayer<AutodeskLayerApiModel & WithLayer & WithPlotStyle & WithLineweight & WithColorBook>({
    autodeskLayerId: Number(autodeskLayerId),
    include: [
      AutodeskLayerRelationship.LAYER,
      AutodeskLayerRelationship.LINEWEIGHT,
      AutodeskLayerRelationship.COLOR_BOOK,
      AutodeskLayerRelationship.TEMPLATE,
      AutodeskLayerRelationship.SYMBOL,
      AutodeskLayerRelationship.PLOT_STYLE,
    ],
  });
  const [createAutodeskLayer, { loading: creatingAutodeskLayer, error: createError }] = useCreateAutodeskLayer();
  const [updateAutodeskLayer, { loading: updatingAutodeskLayer, error: updateError }] = useUpdateAutodeskLayer();
  const { loading: loadingSymbols, response: symbolsResponse } = useSearchSymbols({});
  const { loading: loadingColorBooks, response: colorBooksResponse } = useSearchColorBooks({});
  const { loading: loadingTemplates, response: templatesResponse } = useTemplates({});
  const { loading: loadingLineweights, response: lineweightsResponse } = useLineweights({});
  const { loading: loadingPlotStyles, response: plotStylesResponse } = usePlotStyles({});

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isSubmitting },
  } = useForm<AutodeskLayerForm>({
    resolver: classValidatorResolver(AutodeskLayerForm),
    values: autodeskLayer
      ? { ...autodeskLayer, color: Number(autodeskLayer.color), ...(autodeskLayer.layer ?? {}) }
      : undefined,
    defaultValues: { isPlot: true },
  });

  const onSubmit: SubmitHandler<AutodeskLayerForm> = async (submittedValues) => {
    if (autodeskLayer) {
      const { symbol, colorBook, template, lineweight, plotStyle, ...values } = getDirtyValues(
        dirtyFields,
        submittedValues,
      );
      const relationValues = {
        symbolId: symbol?.id,
        colorBookId: colorBook?.id,
        templateId: template?.id,
        lineweightId: lineweight?.id,
        plotStyleId: plotStyle !== undefined ? (plotStyle?.id ?? null) : undefined,
      };
      await updateAutodeskLayer({
        ...values,
        ...relationValues,
        autodeskLayerId: autodeskLayer.id,
        color: values.color ? `${values.color}` : undefined,
      });
    } else {
      const { symbol, colorBook, template, lineweight, plotStyle, ...values } = submittedValues;
      const relationValues = {
        symbolId: symbol.id,
        colorBookId: colorBook?.id ?? null,
        templateId: template.id,
        lineweightId: lineweight.id,
        plotStyleId: plotStyle?.id ?? null,
      };
      await createAutodeskLayer({ ...submittedValues, ...relationValues, color: `${values.color}` });
    }

    navigate('..');
  };

  const renderContent = () => {
    if (gettingError) {
      return <Alert severity="error">{gettingError?.response?.data.message}</Alert>;
    }

    const isDisabled = creatingAutodeskLayer || updatingAutodeskLayer;

    return (
      <div>
        <TextField
          {...register('name')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={isDisabled}
          error={Boolean(errors.name)}
          helperText={errors.name?.message}
          label="Name"
          margin="dense"
          size="small"
          fullWidth
        />
        <Controller
          control={control}
          name="template"
          render={({ field }) => (
            <Autocomplete
              {...register('template')}
              loading={loadingTemplates}
              sx={{ mt: '1rem' }}
              onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
              onBlur={field.onBlur}
              value={field.value}
              getOptionLabel={(template) => template?.name ?? ''}
              options={templatesResponse?.pages.map(({ result }) => result).flat() ?? []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Template"
                  error={Boolean(errors.template)}
                  helperText={errors.template?.message}
                />
              )}
            />
          )}
        />
        <Controller
          control={control}
          name="colorBook"
          render={({ field }) => (
            <Autocomplete
              {...register('colorBook', { setValueAs: (value) => value || null })}
              loading={loadingColorBooks}
              sx={{ mt: '1rem' }}
              onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
              onBlur={field.onBlur}
              value={field.value}
              getOptionLabel={(colorBook) => colorBook?.name ?? ''}
              options={colorBooksResponse?.pages.map(({ result }) => result).flat() ?? []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Color Book"
                  error={Boolean(errors.colorBook)}
                  helperText={errors.colorBook?.message}
                />
              )}
            />
          )}
        />
        <TextField
          {...register('color', { valueAsNumber: true })}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={isDisabled}
          error={Boolean(errors.color)}
          helperText={errors.color?.message}
          label="Color"
          margin="dense"
          size="small"
          type="number"
          fullWidth
        />
        <Controller
          control={control}
          name="symbol"
          render={({ field }) => (
            <Autocomplete
              {...register('symbol')}
              loading={loadingSymbols}
              sx={{ mt: '1rem' }}
              onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
              onBlur={field.onBlur}
              value={field.value}
              getOptionLabel={(symbol) => symbol?.name ?? ''}
              options={symbolsResponse?.pages.map(({ result }) => result).flat() ?? []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Linetype"
                  error={Boolean(errors.symbol)}
                  helperText={errors.symbol?.message}
                />
              )}
            />
          )}
        />
        <Controller
          control={control}
          name="lineweight"
          render={({ field }) => (
            <Autocomplete
              {...register('lineweight')}
              loading={loadingLineweights}
              sx={{ mt: '1rem' }}
              onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
              onBlur={field.onBlur}
              value={field.value}
              getOptionLabel={(lineweight) => (lineweight?.weight ? `${lineweight?.weight}` : '')}
              options={lineweightsResponse?.pages.map(({ result }) => result).flat() ?? []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Lineweight"
                  error={Boolean(errors.lineweight)}
                  helperText={errors.lineweight?.message}
                />
              )}
            />
          )}
        />
        <Box display="flex" mt={2} gap={2}>
          <FormControlLabel
            sx={{ ml: 0 }}
            label="Plot:"
            labelPlacement="start"
            control={
              <Controller
                control={control}
                name="isPlot"
                render={({ field }) => <Checkbox {...register('isPlot')} checked={Boolean(field.value)} />}
              />
            }
          />
          <Controller
            control={control}
            name="plotStyle"
            render={({ field }) => (
              <Autocomplete
                {...register('plotStyle', { setValueAs: (value) => value || null })}
                loading={loadingPlotStyles}
                sx={{ flex: 1 }}
                onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
                onBlur={field.onBlur}
                value={field.value}
                getOptionLabel={(plotStyle) => plotStyle?.name ?? ''}
                options={plotStylesResponse?.pages.map(({ result }) => result).flat() ?? []}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Plot Style"
                    error={Boolean(errors.plotStyle)}
                    helperText={errors.plotStyle?.message}
                  />
                )}
              />
            )}
          />
        </Box>
        <Box display="flex" gap="1rem" mt={2}>
          <TextField
            {...register('transparency', { valueAsNumber: true })}
            variant="outlined"
            slotProps={{ htmlInput: { min: 0, max: 90 } }}
            sx={{ mt: '1rem' }}
            disabled={isDisabled}
            error={Boolean(errors.transparency)}
            helperText={errors.transparency?.message}
            label="Transparency"
            margin="dense"
            size="small"
            type="number"
            fullWidth
          />
          <FormControlLabel
            sx={{ ml: 0 }}
            label="New Viewport Freeze:"
            labelPlacement="start"
            control={
              <Controller
                control={control}
                name="isNewViewportFreeze"
                render={({ field }) => <Checkbox {...register('isNewViewportFreeze')} checked={Boolean(field.value)} />}
              />
            }
          />
        </Box>
        <TextField
          {...register('description')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={isDisabled}
          error={Boolean(errors.description)}
          helperText={errors.description?.message}
          label="Description"
          margin="dense"
          size="small"
          fullWidth
          multiline
          rows={4}
        />
      </div>
    );
  };

  if (loadingAutodeskLayer) {
    // FIXME: workaround for react-use-form not resetting controlled fields in a dialog
    return <LoadingIndicator />;
  }

  return (
    <Dialog open={true}>
      {loadingAutodeskLayer ? (
        <LoadingIndicator />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} style={{ width: 600 }}>
          <DialogTitle>{`${autodeskLayer ? 'Edit' : 'Add'} CAD Layer`}</DialogTitle>
          <DialogContent>
            {renderContent()}
            {createError || updateError ? (
              <Alert sx={{ mt: '1rem' }} severity="error">
                {createError?.response?.data?.message || updateError?.response?.data?.message}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
              Cancel
            </Button>
            <LoadingButton
              color="primary"
              type="submit"
              variant="contained"
              loading={isSubmitting || Boolean(gettingError)}
            >
              {autodeskLayer ? 'Save' : 'Create'}
            </LoadingButton>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};
